// v0.4.82 — Country-return pill chips, contradiction-fixed.
//
// Operator sign-off 2026-06-19 (choices a/x/p/m):
//   (a) "balanced" headline NEVER coexists with !ready_to_file.
//       When the return is blocked and there's no monetary
//       headline, label it "blocked" instead. Money headlines
//       ("€X due" / "€X credit") still take priority when present.
//   (x) Active blocked chip gains a "Resolve below ↓" anchor
//       button that scrolls the page to the Action-required
//       banner. Symmetric affordance with the inactive chips'
//       "Resolve →" button.
//   (p) Per-chip rule-hint text below the row is filtered to the
//       ACTIVE country only — never the full DE/ES/FR/IT dump
//       that surfaced on every page.
//   (m) Multiple warning conditions (blocked + input_vat_unset)
//       collapse to a SINGLE ⚠ glyph with a tooltip enumerating
//       every active reason. No more "⚠ ⚠" guess-the-meaning.
//
// Previous version (v0.4.77) shipped the orthogonal-status bug:
// a chip could read "FR · CA3 · balanced ⚠" simultaneously
// because "balanced" was money-derived and ⚠ was
// filing-readiness-derived. Operator screenshot 2026-06-19.
// v0.4.95 — Honest action labels on country chips (sign-off
// 2026-06-20 = B + B-extra). Operator dogfood: "Resolve →"
// promised a one-click fix but actually deep-linked to the
// breached return where the operator still had to do the
// real work via Inspect/Flag. Same labelling mismatch the
// pre-v0.4.85 "Amend rate table →" button shipped with.
//
// Renamed:
//   Resolve →       → Open to fix →
//   Resolve below ↓ → Fix below ↓
// Tooltips reworded to describe the navigation outcome, not a
// fictional one-click resolve. Per-chip testid + scroll handler
// preserved; v0.4.82 pins reconciled to the new labels.
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { returnState } from "../lib/returnState";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowRight, Warning as WarningIcon } from "@phosphor-icons/react";
import { getRuleCopy } from "../lib/ruleCopy";

const breachHint = (chip) => {
  // v0.4.85 — route through the shared rule-copy translator so
  // the chip-row breach hint reads the same plain English as the
  // banner below it (sign-off 2026-06-19, option c). Previously
  // this rendered the backend's `description` field raw, leaving
  // an engineer-speak line directly above the translated banner.
  const failures = chip.reconciliation_failures || [];
  if (failures.length > 0) {
    const first = failures[0];
    const copy = getRuleCopy(first.name, first.description || first.error);
    const more = failures.length > 1 ? ` (+${failures.length - 1} more)` : "";
    return `${copy.hint}${more}`;
  }
  if (chip.rate_table_error) {
    return `Rate-table error: ${chip.rate_table_error}`;
  }
  if (chip.input_vat_unset) {
    return "Output VAT present but no input VAT entered — review the deductible-input box.";
  }
  return "Filing checks failed — open this return for details.";
};

// v0.4.82 (m) — collapse all active warning conditions into a
// single tooltip. Returns null when no warnings are active so the
// caller can suppress the ⚠ glyph entirely.
const warningTooltip = (chip) => {
  const reasons = [];
  if (!chip.ready_to_file) reasons.push("Cannot file — calculation checks failed.");
  if (chip.input_vat_unset) reasons.push("Output VAT present but no input VAT entered yet — review the deductible-input box.");
  if (chip.rate_table_error) reasons.push(`Rate-table error: ${chip.rate_table_error}`);
  if (reasons.length === 0) return null;
  if (reasons.length === 1) return `⚠ ${reasons[0]}`;
  return `⚠ Action required — ${reasons.length} issues:\n` +
    reasons.map((r, i) => `${i + 1}. ${r}`).join("\n");
};

// v0.4.82 (x) — scroll the page to the Action-required banner.
// Banner data-testid is "reconciliation-failures" (ActionRequiredBanner.jsx).
const scrollToBanner = () => {
  const el = document.querySelector('[data-testid="reconciliation-failures"]');
  if (el && typeof el.scrollIntoView === "function") {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

// v0.5.0i+12 — Country pill prominence pass. Operator brief:
// the per-country chips were "far too small and very difficult
// to notice". Three-line redesign:
//   1. Bigger pill        — px-3 py-1.5 → px-5 py-3.5, text-xs → text-sm/base.
//   2. Stronger borders   — border-2 always (was border-1 on muted state),
//                           shadow-sm idle / shadow-lg on hover.
//   3. Colour-coded by    — rose for blocked, amber for "to pay",
//      state (real-world    teal for "to reclaim", slate for "nothing
//      action priority)     to file". Nothing-to-file chips no longer
//                           opacity-80'd into invisibility.
// The country code now leads bold + uppercase + tracking-wide so
// it scans as the primary identifier. Headline is bold so the
// money figure dominates. Warning icon promoted from inline glyph
// to a proper Phosphor <Warning> component.

// v0.5.0i+17 — currency-aware headline. UK returns file in GBP,
// every EU MS in EUR. The chip payload now carries an ISO-4217
// `currency` field from the backend (see _country_return_summary.py
// _return_currency); we translate it to a symbol here and fall
// back to € when the field is missing (older frozen calcs from
// before v0.5.0i+17 don't have it). Reported bug: "UK VAT returns
// get calculated in Euros???" — values were correct, the pill was
// mis-labelled.
const _CURRENCY_SYMBOL = {
  GBP: "£", EUR: "€", USD: "$",
  // v0.5.9y — non-euro EU national filing currencies.
  DKK: "kr", SEK: "kr", CZK: "Kč", PLN: "zł", HUF: "Ft",
  RON: "lei", BGN: "лв",
};
const _symbolFor = (currency) => _CURRENCY_SYMBOL[currency] || "€";

const headlineFor = (c) => {
  const due = parseFloat(c.total_due || "0");
  const credit = parseFloat(c.total_credit || "0");
  const sym = _symbolFor(c.currency);
  if (due > 0) {
    return {
      text: `${sym}${due.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} to pay`,
      tone: "due",
    };
  }
  if (credit > 0) {
    return {
      text: `${sym}${credit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} to reclaim`,
      tone: "credit",
    };
  }
  if (!c.ready_to_file) return { text: "cannot file", tone: "blocked" };
  // v0.6.9 — found live: a country with real reportable turnover (e.g.
  // GB Box 6 export/outside-scope turnover) but zero VAT due/credit
  // used to fall straight to "nothing to file" — misleading, since a
  // VAT-registered business still owes a return disclosing that
  // turnover even when no VAT is payable. has_activity (backend,
  // _country_row_has_activity — the same signal that decided this
  // chip appears at all) distinguishes a genuine nil return from a
  // country that truly has nothing to report.
  if (c.has_activity) return { text: "nil return due", tone: "idle" };
  return { text: "nothing to file", tone: "idle" };
};

// Visual tone → tailwind class bundle. Active state layers a ring
// on top of the base. Hover always lifts + shadows so the click
// affordance is obvious even on idle chips.
const wrapToneClasses = (tone, isActive) => {
  const base = "transition-all hover:shadow-lg hover:-translate-y-[2px]";
  if (tone === "blocked") {
    return `${base} bg-rose-50 dark:bg-rose-950/40 border-2 border-rose-500 dark:border-rose-500 text-rose-900 dark:text-rose-50 shadow-md shadow-rose-500/20 hover:bg-rose-100 dark:hover:bg-rose-900/50 ${
      isActive ? "ring-2 ring-rose-400/60 dark:ring-rose-500/40" : ""
    }`;
  }
  if (tone === "due") {
    return `${base} bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-500 dark:border-amber-500 text-amber-900 dark:text-amber-50 shadow-md shadow-amber-500/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 ${
      isActive ? "ring-2 ring-amber-400/60 dark:ring-amber-500/40" : ""
    }`;
  }
  if (tone === "credit") {
    return `${base} bg-teal-50 dark:bg-teal-900/40 border-2 border-teal-500 dark:border-teal-500 text-teal-900 dark:text-teal-50 shadow-md shadow-teal-500/20 hover:bg-teal-100 dark:hover:bg-teal-900/50 ${
      isActive ? "ring-2 ring-teal-400/60 dark:ring-teal-500/40" : ""
    }`;
  }
  // idle — nothing to file. Still a clearly visible card; just no
  // colour emphasis since the operator has no action here.
  return `${base} bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:border-slate-400 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800/60 ${
    isActive ? "ring-2 ring-slate-400/40 dark:ring-slate-500/40" : ""
  }`;
};

export const CountryReturnTabs = ({ calcId, countries, active }) => {
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  if (!countries || countries.length === 0) return null;
  // v0.4.82 (p) — only the active country's hint surfaces below
  // the row. Operators on the FR page shouldn't be reading the
  // DE/ES/IT rule lines.
  const activeChip = countries.find((c) => c.country === active);
  const showActiveHint = activeChip && !activeChip.ready_to_file;
  return (
    <div data-testid="country-return-tabs" className="space-y-3 mb-5">
      <div className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200">
        {t("ui.country_return.tabs_heading", { defaultValue: "National returns — click a country to open" })}
      </div>
      <div className="flex flex-wrap gap-3">
        {countries.map((c) => {
          const isActive = c.country === active;
          const headline = headlineFor(c);
          // tone falls through to "blocked" if the return is not
          // ready, even when there's money activity — the blocked
          // state always wins (you can't file regardless).
          const tone = !c.ready_to_file ? "blocked" : headline.tone;
          const blocked = tone === "blocked";
          const tooltip = warningTooltip(c);
          // A2-581 lead 18: the chip's buttons sit inside a clickable wrapper,
          // so one click navigated twice (two history entries, Back needed
          // two presses), and the open chip navigated to its own page.
          const goto = (e) => {
            e?.stopPropagation?.();
            if (isActive) return;
            nav(`/calc/${calcId}/country-return/${c.country}`, { state: returnState(location) });
          };
          return (
            <div
              key={c.country}
              data-testid={`country-tab-wrap-${c.country}`}
              data-state={blocked ? "needs-action" : "ready"}
              data-active={isActive ? "true" : "false"}
              data-tone={tone}
              className={`inline-flex items-stretch rounded-xl cursor-pointer ${wrapToneClasses(tone, isActive)}`}
              onClick={!isActive ? goto : undefined}
            >
              <button
                data-testid={`country-tab-${c.country}`}
                onClick={goto}
                title={tooltip || undefined}
                className="px-5 py-3 text-sm sm:text-base tracking-normal flex items-center gap-2.5"
              >
                <span className="font-bold uppercase tracking-wider text-base sm:text-lg">
                  {c.country}
                </span>
                <span className="opacity-50">·</span>
                <span className="font-medium tabular-nums text-xs sm:text-sm opacity-80">
                  {c.form_code}
                </span>
                <span className="opacity-50">·</span>
                <span className="font-bold">{headline.text}</span>
                {tooltip && (
                  <WarningIcon
                    data-testid={`country-tab-warn-${c.country}`}
                    size={18}
                    weight="fill"
                    className={`ml-1 ${
                      blocked
                        ? "text-rose-700 dark:text-rose-300"
                        : "text-amber-600 dark:text-amber-400"
                    }`}
                  />
                )}
              </button>
              {blocked && !isActive && (
                <button
                  type="button"
                  data-testid={`country-tab-resolve-${c.country}`}
                  data-resolution-class="self-resolve"
                  data-resolution-endpoint="navigation"
                  onClick={goto}
                  className="border-l-2 border-rose-400 dark:border-rose-600 px-4 py-3 text-xs sm:text-sm font-semibold tracking-normal text-rose-800 dark:text-rose-100 hover:bg-rose-100 dark:hover:bg-rose-900/50 rounded-r-xl flex items-center gap-1.5"
                  title={`Open the ${c.country} ${c.form_code} return — you'll see the flagged rows and fix the issue there`}
                >
                  {t("ui.country_return.tabs.open_and_review", { defaultValue: "Open and review" })}
                  <ArrowRight size={14} weight="bold" />
                </button>
              )}
              {blocked && isActive && (
                <button
                  type="button"
                  data-testid={`country-tab-resolve-below-${c.country}`}
                  data-resolution-class="self-resolve"
                  data-resolution-endpoint="navigation"
                  onClick={scrollToBanner}
                  className="border-l-2 border-rose-400 dark:border-rose-600 px-4 py-3 text-xs sm:text-sm font-semibold tracking-normal text-rose-800 dark:text-rose-100 hover:bg-rose-100 dark:hover:bg-rose-900/50 rounded-r-xl flex items-center gap-1.5"
                  title={t("ui.country_return.tabs.jump_to_issues_title", { defaultValue: "Jump to the issues banner on this page" })}
                >
                  {t("ui.country_return.tabs.jump_to_issues", { defaultValue: "Jump to issues" })}
                  <ArrowDown size={14} weight="bold" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {/* v0.4.82 (p) — single hint line, active country only. */}
      {showActiveHint && (
        <div
          data-testid={`country-tab-breach-hint-${activeChip.country}`}
          className="text-xs text-rose-700 dark:text-rose-300 leading-snug pl-2"
        >
          <span className="text-rose-500 dark:text-rose-400 mr-1">↳</span>
          <span className="font-semibold mr-1">{activeChip.country}:</span>
          {breachHint(activeChip)}
        </div>
      )}
    </div>
  );
};

export default CountryReturnTabs;
